function escapePattern(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function mentionsNickname(body: string | null, nickname: string | null | undefined) {
  if (!body || !nickname) return false;
  const normalizedBody = body.normalize("NFKC");
  const normalizedNickname = nickname.normalize("NFKC");
  return new RegExp(`(?:^|[^\\p{L}\\p{N}_-])@${escapePattern(normalizedNickname)}(?![\\p{L}\\p{N}_-])`, "iu").test(normalizedBody);
}

export function shouldShowLobbyMessage(
  message: { author: { id: string }; body: string | null },
  viewer: { id: string; nickname: string } | null,
  blockedIds: ReadonlySet<string>,
  focusEnabled: boolean,
  focusIds: ReadonlySet<string>,
) {
  if (blockedIds.has(message.author.id)) return false;
  if (!focusEnabled || !viewer) return true;
  return message.author.id === viewer.id
    || focusIds.has(message.author.id)
    || mentionsNickname(message.body, viewer.nickname);
}

export function isAfterLocalClear(message: { seq: number }, clearedThrough: number) {
  return message.seq > Math.max(0, clearedThrough);
}

export function localClearStorageKey(viewerId: string | null | undefined, roomId: string) {
  const identity = viewerId?.trim() ? encodeURIComponent(viewerId) : "anonymous";
  return `kiwichat:clear-view:v1:${identity}:${encodeURIComponent(roomId)}`;
}

export function parseLocalClearSequence(value: string | null) {
  if (value === null || value.trim() === "") return 0;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0;
}

export function isConversationTrace(message: { deletedAt: number | null; recalledAt?: number | null }) {
  return message.deletedAt == null && message.recalledAt == null;
}

export type RoomActivitySample = { at: number; authorId: string };

export const FOCUS_ACTIVITY_WINDOW_MS = 60_000;
export const FOCUS_ACTIVITY_MESSAGE_THRESHOLD = 24;
export const FOCUS_ACTIVITY_AUTHOR_THRESHOLD = 6;

export function recentRoomActivity(samples: readonly RoomActivitySample[], now: number) {
  return samples.filter((sample) => sample.at >= now - FOCUS_ACTIVITY_WINDOW_MS && sample.at <= now);
}

export function shouldSuggestFocus(samples: readonly RoomActivitySample[], now: number) {
  const recent = recentRoomActivity(samples, now);
  if (recent.length < FOCUS_ACTIVITY_MESSAGE_THRESHOLD) return false;
  return new Set(recent.map((sample) => sample.authorId)).size >= FOCUS_ACTIVITY_AUTHOR_THRESHOLD;
}

export function roomStreamMessages<T extends { seq: number; author: { id: string } }>(
  messages: Iterable<T>,
  blockedIds: ReadonlySet<string>,
) {
  return [...messages]
    .filter((message) => !blockedIds.has(message.author.id))
    .sort((left, right) => left.seq - right.seq);
}
